import React from 'react';
import {
  BanknotesIcon,
  CheckCircleIcon,
  LockClosedIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const STAGES = [
  { key: 'prequal', label: 'Personal prequalification' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'packaging', label: 'Capital packaging' },
  { key: 'ready', label: 'Deal-ready' },
];

export default function CapitalReadinessCard({ loanApplicationId, profile = {}, onChange }) {
  const [saving, setSaving] = React.useState(false);
  const engagementSigned = profile.engagementSigned === true;
  const engagementPaid = profile.engagementPaid === true;
  const packagingUnlocked = engagementSigned && engagementPaid;
  const stageIndex = profile.capitalPackagingComplete ? 3 : (packagingUnlocked ? 2 : (engagementSigned ? 1 : 0));

  const setCapitalNeed = async (capitalNeed) => {
    if (!loanApplicationId || saving) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'loanApplications', loanApplicationId), {
        capitalNeed,
        capitalNeedUpdatedAt: serverTimestamp(),
      });
      onChange?.({ ...profile, capitalNeed });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm">
      <div className="bg-gradient-to-r from-slate-950 via-indigo-950 to-blue-900 px-6 py-6 text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-200">Capital readiness</p>
            <h2 className="mt-2 text-2xl font-bold">Prepare your financing before the deal.</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
              We prequalify you first, then build the lender and investor package so you can move quickly when the right business appears.
            </p>
          </div>
          <span className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${packagingUnlocked ? 'bg-emerald-400/15 text-emerald-200 ring-1 ring-emerald-300/30' : 'bg-white/10 text-white ring-1 ring-white/20'}`}>
            {packagingUnlocked ? <CheckCircleIcon className="h-4 w-4" /> : <LockClosedIcon className="h-4 w-4" />}
            {packagingUnlocked ? 'Packaging unlocked' : 'Prequalification stage'}
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {STAGES.map((stage, index) => (
            <div key={stage.key} className="min-w-0">
              <div className={`h-1.5 rounded-full ${index <= stageIndex ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <p className={`mt-2 text-xs font-semibold ${index <= stageIndex ? 'text-gray-900' : 'text-gray-400'}`}>{stage.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <p className="text-sm font-semibold text-gray-900">What kind of capital should we prepare?</p>
          <p className="mt-1 text-sm text-gray-500">You can change this as your acquisition strategy develops.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <button type="button" disabled={saving} onClick={() => setCapitalNeed('debt_only')} className={`rounded-xl border p-4 text-left transition ${profile.capitalNeed === 'debt_only' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
              <BanknotesIcon className="h-6 w-6 text-blue-600" />
              <p className="mt-3 text-sm font-bold text-gray-900">Debt financing</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">SBA and conventional lender preparation.</p>
            </button>
            <button type="button" disabled={saving} onClick={() => setCapitalNeed('debt_and_investors')} className={`rounded-xl border p-4 text-left transition ${profile.capitalNeed === 'debt_and_investors' ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500' : 'border-gray-200 hover:border-gray-300'}`}>
              <UserGroupIcon className="h-6 w-6 text-violet-600" />
              <p className="mt-3 text-sm font-bold text-gray-900">Debt + investors</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">Lender package plus investor-readiness and equity planning.</p>
            </button>
          </div>
        </div>

        {!packagingUnlocked && (
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <LockClosedIcon className="mt-0.5 h-5 w-5 flex-none text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-amber-900">The packaging checklist unlocks after engagement.</p>
              <p className="mt-1 text-xs leading-5 text-amber-800">Complete the prequalification documents now. Once your engagement letter is signed and the $5,000 engagement fee clears, the remaining checklist will appear here automatically.</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
